import { useState, useEffect, useCallback } from 'react';
import { reverseGeocode } from '../lib/reverseGeocode';

const LOCATION_CACHE_KEY = 'cached_user_location';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const useLocationStreaming = () => {
  const [displayText, setDisplayText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [finalLocation, setFinalLocation] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [error, setError] = useState(null);

  const getCachedLocation = useCallback(() => {
    try {
      const cached = localStorage.getItem(LOCATION_CACHE_KEY);
      if (cached) {
        const { location, coordinates, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < CACHE_DURATION) {
          return { location, coordinates };
        }
      }
    } catch (error) {
      console.error('Error reading cached location:', error);
    }
    return null;
  }, []);

  const setCachedLocation = useCallback((location, coordinates) => {
    try {
      const cacheData = {
        location,
        coordinates,
        timestamp: Date.now()
      };
      localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching location:', error);
    }
  }, []);

  const streamText = useCallback((text) => {
    // Respect prefers-reduced-motion: skip the char-by-char reveal entirely.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setDisplayText(text);
      setIsStreaming(false);
      return () => {};
    }

    setIsStreaming(true);
    setDisplayText('');

    let currentIndex = 0;
    const streamInterval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayText(text.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(streamInterval);
        setIsStreaming(false);
      }
    }, 50); // Medium speed - adjust as needed

    return () => clearInterval(streamInterval);
  }, []);

  const fetchLocation = useCallback(async () => {
    // Check cache first
    const cached = getCachedLocation();
    if (cached) {
      setCoordinates(cached.coordinates);
      setFinalLocation(cached.location);
      streamText(cached.location);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get user's geolocation
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5 * 60 * 1000 // 5 minutes
          }
        );
      });

      const { latitude, longitude } = position.coords;
      setCoordinates({ latitude, longitude });

      // Reverse geocode to get location name
      const locationName = await reverseGeocode(latitude, longitude);
      
      if (locationName) {
        setFinalLocation(locationName);
        setCachedLocation(locationName, { latitude, longitude });
        streamText(locationName);
      } else {
        // Fallback to coordinates
        const coordsText = `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
        setFinalLocation(coordsText);
        setCachedLocation(coordsText, { latitude, longitude });
        streamText(coordsText);
      }
    } catch (error) {
      console.error('Location fetch failed:', error);
      setError(error);
      // Don't set any text, let parent component handle fallback
    } finally {
      setIsLoading(false);
    }
  }, [getCachedLocation, setCachedLocation, streamText]);

  const resetLocation = useCallback(() => {
    setDisplayText('');
    setFinalLocation('');
    setCoordinates(null);
    setError(null);
    setIsLoading(false);
    setIsStreaming(false);
  }, []);

  return {
    displayText,
    finalLocation,
    coordinates,
    isLoading,
    isStreaming,
    error,
    fetchLocation,
    resetLocation
  };
};